/**
 * Per-case investigations store provider.
 *
 * Mounts the store keyed by `caseId` (uses the module-level cache so
 * imperative callers — e.g. the agent sidebar — share the same instance).
 * Triggers `fetchAll` on mount so the Graph Views route lands with the
 * saved list already loaded.
 */

import { createContext, type ReactNode, useContext, useEffect, useMemo } from 'react'
import { useStore } from 'zustand'
import type { StoreApi } from 'zustand/vanilla'
import { getOrCreateInvestigationsStore, type InvestigationsState } from './investigations-store'

const InvestigationsStoreContext = createContext<StoreApi<InvestigationsState> | null>(null)

interface InvestigationsStoreProviderProps {
  caseId: string
  children: ReactNode
}

export function InvestigationsStoreProvider({
  caseId,
  children,
}: InvestigationsStoreProviderProps) {
  const store = useMemo(() => getOrCreateInvestigationsStore({ caseId }), [caseId])

  useEffect(() => {
    store.getState().fetchAll()
  }, [store])

  return (
    <InvestigationsStoreContext.Provider value={store}>
      {children}
    </InvestigationsStoreContext.Provider>
  )
}

export function useInvestigationsStore<T>(selector: (state: InvestigationsState) => T): T {
  const store = useContext(InvestigationsStoreContext)
  if (!store) {
    throw new Error('useInvestigationsStore must be used inside <InvestigationsStoreProvider>')
  }
  return useStore(store, selector)
}

export function useInvestigationsStoreApi(): StoreApi<InvestigationsState> {
  const store = useContext(InvestigationsStoreContext)
  if (!store) {
    throw new Error('useInvestigationsStoreApi must be used inside <InvestigationsStoreProvider>')
  }
  return store
}

/** Optional accessor — returns null instead of throwing when no provider is
 * mounted. Used by hooks that may render outside `CaseLayout`. */
export function useInvestigationsStoreApiOptional(): StoreApi<InvestigationsState> | null {
  return useContext(InvestigationsStoreContext)
}
